import OlIntersects from 'ol/format/filter/Intersects';
import { reverseDate } from '../../utilities/utilities';

export function opengisNamespace(version) {
    if (version < 2) {
        return 'http://www.opengis.net/ogc';
    }
    return 'http://www.opengis.net/fes/2.0';
}
/**
* Stel de filter URL samen indien aanwezig, anders null.
* Wordt gebruikt bij het toevoegen van een laag aan de kaart
* If WebService can be used directly, return service uri
* Otherwise, if there is a search-filter or attributefilter active, go via proxy and send encoded CQL-filter with WMS request
* @param {*}        data            [query data]
* @param {string}   serviceType     [optional parameter, if ommited, this.serviceType is taken]
* @return {string}  [filter url]
*/
export const getFilterUrl = function (data, serviceType, filterQuery) {
    if (useOgcWebServiceDirectly(data)) {
        if (!serviceType) {
            serviceType = data.serviceType;
        }
        // PBI 13931: Dashboard: Ondersteunen OWS met aparte URL's voor WMS, WMTS en WFS
        // Make difference between WNS and WFS request Url
        return GetServiceUrl(serviceType, data);
    }

    if (data) {
        const baseUrl = data.customEndPoints[serviceType.toLocaleLowerCase()].url;
        // In case of a CQL/OGC filter
        if (filterQuery && filterQuery.length > 0) {
            return `${baseUrl}/${filterQuery}`;
        }
    }
    return null;
};

export const useOgcWebServiceDirectly = function (layerData) {
    // Use proxy in these cases
    if (layerData.isSecured) {
        return false;
    }
    if (layerData.serviceUri.indexOf('https://') === -1) {
        return false;
    }
    if (layerData.filterCQL && layerData.filterCQL.length > 0 && !layerData.graphicalLayerName) {
        return false;
    }
    if (layerData.serviceType === 'WFS' && !layerData.graphicalLayerName) {
        return false;
    }
    return layerData.feedback ? false : true;
};

export const GetServiceUrl = function (serviceType, { source, serviceUri }) {
    // PBI 13931: Dashboard: Ondersteunen OWS met aparte URL's voor WMS, WMTS en WFS
    // Make difference between WNS and WFS request Url
    if (serviceType === 'WFS'
        && source.uri_WFS && source.uri_WFS.length > 0) {
        return source.uri_WFS;
    }
    if (serviceType === 'WMS'
        && source.uri_WMS && source.uri_WMS.length > 0) {
        return source.uri_WMS;
    }
    if (serviceType === 'WMTS'
        && source.uri_WMTS && source.uri_WMTS.length > 0) {
        return source.uri_WMTS;
    }
    // fallback to the service url
    return serviceUri;
};

/**
 * Bind filters together with AND operators
 * @param  {array} filters [filter object array]
 * @param  {object} ogcAnd  [xml AND object]
 */
export function ogcCreateFilterValues(version, filters, ogcAnd, isFeedback = false) {
    // traverse all filters
    filters.forEach(filter => {
        if (filter) {
            if (filter.dataType === 'xsd:dateTime') {
                ogcCreateDateTimeValues(version, filter, ogcAnd);
            } else if (filter.dataType === 'xsd:date') {
                ogcCreateDateValues(version, filter, ogcAnd);
            } else {
                // Turn filter into property operator in OGC Filter
                const propertyoperator = ogcPropertyOperatorFromFilter(version, filter);
                // Add multiple parts to ogcAnd element
                ogcAnd.appendChild(propertyoperator);
            }
        }
    });
}

function ogcCreateDateTimeValues(version, filter, ogcAnd) {
    const filterValueMin = `${reverseDate(filter.value)}T00:00:00Z`;
    const filterValueMax = `${reverseDate(filter.value)}T23:59:59Z`;

    const createdOpengisNamespace = opengisNamespace(version);
    let propertyoperator;

    switch (filter.operator) {
        case '>':
            propertyoperator = document.createElementNS(createdOpengisNamespace, 'PropertyIsGreaterThan');
            ogcSetFilterPropertyName(version, filter, propertyoperator);
            ogcSetFilterPropertyLiteral(version, filterValueMax, propertyoperator);
            ogcAnd.appendChild(propertyoperator);
            break;
        case '<':
            propertyoperator = document.createElementNS(createdOpengisNamespace, 'PropertyIsLessThan');
            ogcSetFilterPropertyName(version, filter, propertyoperator);
            ogcSetFilterPropertyLiteral(version, filterValueMin, propertyoperator);
            ogcAnd.appendChild(propertyoperator);
            break;
        case '>=':
            propertyoperator = document.createElementNS(createdOpengisNamespace, 'PropertyIsGreaterThanOrEqualTo');
            ogcSetFilterPropertyName(version, filter, propertyoperator);
            ogcSetFilterPropertyLiteral(version, filterValueMin, propertyoperator);
            ogcAnd.appendChild(propertyoperator);
            break;
        case '<=':
            propertyoperator = document.createElementNS(createdOpengisNamespace, 'PropertyIsLessThanOrEqualTo');
            ogcSetFilterPropertyName(version, filter, propertyoperator);
            ogcSetFilterPropertyLiteral(version, filterValueMax, propertyoperator);
            ogcAnd.appendChild(propertyoperator);
            break;
        case '<>': {
            propertyoperator = document.createElementNS(createdOpengisNamespace, 'PropertyIsLessThan');
            const propertyoperator1 = document.createElementNS(createdOpengisNamespace, 'PropertyIsGreaterThan');

            ogcSetFilterPropertyName(version, filter, propertyoperator);
            ogcSetFilterPropertyLiteral(version, filterValueMin, propertyoperator);

            ogcSetFilterPropertyName(version, filter, propertyoperator1);
            ogcSetFilterPropertyLiteral(version, filterValueMax, propertyoperator1);

            const ogcOr = document.createElementNS(createdOpengisNamespace, 'Or');
            ogcOr.appendChild(propertyoperator);
            ogcOr.appendChild(propertyoperator1);
            ogcAnd.appendChild(ogcOr);
            break;
        }
        case '=':
        default: {
            propertyoperator = document.createElementNS(createdOpengisNamespace, 'PropertyIsGreaterThanOrEqualTo');
            const propertyoperator1 = document.createElementNS(createdOpengisNamespace, 'PropertyIsLessThanOrEqualTo');

            ogcSetFilterPropertyName(version, filter, propertyoperator);
            ogcSetFilterPropertyLiteral(version, filterValueMin, propertyoperator);

            ogcSetFilterPropertyName(version, filter, propertyoperator1);
            ogcSetFilterPropertyLiteral(version, filterValueMax, propertyoperator1);

            ogcAnd.appendChild(propertyoperator);
            ogcAnd.appendChild(propertyoperator1);
        }
    }
}

function ogcCreateDateValues(version, filter, ogcAnd) {
    const dateValue = reverseDate(filter.value);

    const createdOpengisNamespace = opengisNamespace(version);
    let propertyoperator;

    switch (filter.operator) {
        case '>':
            propertyoperator = document.createElementNS(createdOpengisNamespace, 'PropertyIsGreaterThan');
            ogcSetFilterPropertyName(version, filter, propertyoperator);
            ogcSetFilterPropertyLiteral(version, dateValue, propertyoperator);
            ogcAnd.appendChild(propertyoperator);
            break;
        case '<':
            propertyoperator = document.createElementNS(createdOpengisNamespace, 'PropertyIsLessThan');
            ogcSetFilterPropertyName(version, filter, propertyoperator);
            ogcSetFilterPropertyLiteral(version, dateValue, propertyoperator);
            ogcAnd.appendChild(propertyoperator);
            break;
        case '>=':
            propertyoperator = document.createElementNS(createdOpengisNamespace, 'PropertyIsGreaterThanOrEqualTo');
            ogcSetFilterPropertyName(version, filter, propertyoperator);
            ogcSetFilterPropertyLiteral(version, dateValue, propertyoperator);
            ogcAnd.appendChild(propertyoperator);
            break;
        case '<=':
            propertyoperator = document.createElementNS(createdOpengisNamespace, 'PropertyIsLessThanOrEqualTo');
            ogcSetFilterPropertyName(version, filter, propertyoperator);
            ogcSetFilterPropertyLiteral(version, dateValue, propertyoperator);
            ogcAnd.appendChild(propertyoperator);
            break;
        case '<>': {
            propertyoperator = document.createElementNS(createdOpengisNamespace, 'PropertyIsLessThan');
            const propertyoperator1 = document.createElementNS(createdOpengisNamespace, 'PropertyIsGreaterThan');

            ogcSetFilterPropertyName(version, filter, propertyoperator);
            ogcSetFilterPropertyLiteral(version, dateValue, propertyoperator);

            ogcSetFilterPropertyName(version, filter, propertyoperator1);
            ogcSetFilterPropertyLiteral(version, dateValue, propertyoperator1);

            const ogcOr = document.createElementNS(createdOpengisNamespace, 'Or');
            ogcOr.appendChild(propertyoperator);
            ogcOr.appendChild(propertyoperator1);
            ogcAnd.appendChild(ogcOr);
            break;
        }
        case '=':
        default: {
            propertyoperator = document.createElementNS(createdOpengisNamespace, 'PropertyIsEqualTo');

            ogcSetFilterPropertyName(version, filter, propertyoperator);
            ogcSetFilterPropertyLiteral(version, dateValue, propertyoperator);


            ogcAnd.appendChild(propertyoperator);
        }
    }
}

/**
 * Turns the attribute- or search-filter into a property operator
 * @param {object} filter the input filter
 * @returns {object} property operator
 */
export function ogcPropertyOperatorFromFilter(version, filter) {
    // Is the value an array with multiple filter values?
    if (filter.value instanceof Array) {
        if (filter.value.length === 0) {
            return ogcCreateNullOrEmptyOperator(version, filter);
        }
        // create a query with multiple filter values
        if (filter.operator === 'C') {
            return ogcCreatePropertyInOperator(version, filter);
        } else if (filter.operator === 'S') {
            return ogcCreatePropertyStartsWithOperator(version, filter);
        } else if (filter.operator === 'E') {
            return ogcCreatePropertyEndsWithOperator(version, filter);
        } else {
            return ogcCreatePropertyEqualOperator(version, filter);
        }
    } else {
        // Return property operator in OGC Filter
        if (filter.value === '' || filter.value === null) {
            return ogcCreateNullOrEmptyOperator(version, filter);
        }
        // create a query with a single filter value
        return ogcCreateRegularPropertyOperator(version, filter);
    }
}

/**
 * For any equality operator, creates a tag that includes null and empty values
 * For any non-equality operator, creates a tag that excludes null and empty values
 * @param {any} filter the input filter
 * @returns {object} ogc tag to include/excluse empty and null values
 */
function ogcCreateNullOrEmptyOperator(version, filter) {
    switch (filter.operator) {
        // Assume Not equals, fall through
        case '<>':
        case '<':
        case '>':
            return ogcExcludeNullAndEmpty(version, filter);
        // Assume Equals
        case 'C':
        case 'like':
        case '>=':
        case '<=':
        case '=':
        default:
            return ogcIncludeNullOrEmpty(version, filter);
    }
}

/**
 * Create a filter string for contains filter value and operator
 * @param  {*} filter, filter object on which the query ia based on
 * @return {string} query string
 */
function ogcCreatePropertyEqualOperator(version, filter) {
    const createdOpengisNamespace = opengisNamespace(version);
    // create a parent node
    const propertyQuery = document.createElementNS(createdOpengisNamespace, 'Or');
    const length = filter.value.length;
    for (let ix = 0; ix < length; ix++) {
        const value = filter.value[ix];
        // create next operand
        const operand = document.createElementNS(createdOpengisNamespace, 'PropertyIsEqualTo');
        // Set property name in OGC Filter
        ogcSetFilterPropertyName(version, filter, operand);
        // Set property literal in OGC Filter
        ogcSetFilterPropertyLiteral(version, value, operand);
        // Do not use <Or> for only 1 filter value
        if (length === 1) {
            return operand;
        }
        // add to property query
        propertyQuery.appendChild(operand);
    }
    return propertyQuery;
}

/**
 * Create a filter string for contains filter value and operator
 * @param  {*} filter, filter object on which the query ia based on
 * @return {string} query string
 */
function ogcCreatePropertyInOperator(version, filter) {
    const createdOpengisNamespace = opengisNamespace(version);
    // create a parent node
    const propertyQuery = document.createElementNS(createdOpengisNamespace, 'Or');
    const length = filter.value.length;
    for (let ix = 0; ix < length; ix++) {
        const value = filter.value[ix];
        // create next operand
        const operand = document.createElementNS(createdOpengisNamespace, 'PropertyIsLike');
        // wildcard attribute
        operand.setAttribute('wildCard', '%');
        operand.setAttribute('singleChar', '_');
        operand.setAttribute('escapeChar', '\\');
        // Set property name in OGC Filter
        ogcSetFilterPropertyName(version, filter, operand);
        // Set property literal in OGC Filter
        ogcSetFilterPropertyLiteral(version, `%${value}%`, operand);
        // Do not use <Or> for only 1 filter value
        if (length === 1) {
            return operand;
        }
        // add to property query
        propertyQuery.appendChild(operand);
    }
    return propertyQuery;
}
/**
 * Create a filter string for starts with filter value and operator
 * @param  {*} filter, filter object on which the query ia based on
 * @return {string} query string
 */
function ogcCreatePropertyStartsWithOperator(version, filter) {
    const createdOpengisNamespace = opengisNamespace(version);
    // create a parent node
    const propertyQuery = document.createElementNS(createdOpengisNamespace, 'Or');
    const length = filter.value.length;
    for (let ix = 0; ix < length; ix++) {
        const value = filter.value[ix];
        // create next operand
        const operand = document.createElementNS(createdOpengisNamespace, 'PropertyIsLike');
        // wildcard attribute
        operand.setAttribute('wildCard', '%');
        operand.setAttribute('singleChar', '_');
        operand.setAttribute('escapeChar', '\\');
        // Set property name in OGC Filter
        ogcSetFilterPropertyName(version, filter, operand);
        // Set property literal in OGC Filter
        ogcSetFilterPropertyLiteral(version, `${value}%`, operand);
        // Do not use <Or> for only 1 filter value
        if (length === 1) {
            return operand;
        }
        // add to property query
        propertyQuery.appendChild(operand);
    }
    return propertyQuery;
}
/**
 * Create a filter string for ends with filter value and operator
 * @param  {*} filter, filter object on which the query ia based on
 * @return {string} query string
 */
function ogcCreatePropertyEndsWithOperator(version, filter) {
    const createdOpengisNamespace = opengisNamespace(version);
    // create a parent node
    const propertyQuery = document.createElementNS(createdOpengisNamespace, 'Or');
    const length = filter.value.length;
    for (let ix = 0; ix < length; ix++) {
        const value = filter.value[ix];
        // create next operand
        const operand = document.createElementNS(createdOpengisNamespace, 'PropertyIsLike');
        // wildcard attribute
        operand.setAttribute('wildCard', '%');
        operand.setAttribute('singleChar', '_');
        operand.setAttribute('escapeChar', '\\');
        // Set property name in OGC Filter
        ogcSetFilterPropertyName(version, filter, operand);
        // Set property literal in OGC Filter
        ogcSetFilterPropertyLiteral(version, `%${value}`, operand);
        // Do not use <Or> for only 1 filter value
        if (length === 1) {
            return operand;
        }
        // add to property query
        propertyQuery.appendChild(operand);
    }
    return propertyQuery;
}

/**
 * Create a filter string for filtering out soft-deleted feedbacks
 * All feedbacks where verwijderd equals true should not be retrieved.
 * @return {string} query string
 */
export function ogcPropertyOperatorFromSoftDelete(version) {
    const softDeleteFilter = {
        operator: '=',
        value: '',
        filterAttribute: 'verwijderddoor',
        caseSensitive: false,
        dataType: 'xsd:string'
    };
    return ogcCreateNullOrEmptyOperator(version, softDeleteFilter);
}

/**
 * Creates a OGC tag that includes null or empty values
 * @param {any} filter the input filter
 * @returns {object} ogcOr Tag
 */
function ogcIncludeNullOrEmpty(version, filter) {
    const createdOpengisNamespace = opengisNamespace(version);
    const ogcPropIsNull = document.createElementNS(createdOpengisNamespace, 'PropertyIsNull');
    // Set property name in OGC Filter
    ogcSetFilterPropertyName(version, filter, ogcPropIsNull);
    // only for string datatype
    if (filter.dataType.indexOf('string') > -1) {
        const ogcOr = document.createElementNS(createdOpengisNamespace, 'Or');
        ogcOr.appendChild(ogcPropIsNull);
        const ogcPropIsEmpty = document.createElementNS(createdOpengisNamespace, 'PropertyIsEqualTo');
        // Set property name in OGC Filter
        ogcSetFilterPropertyName(version, filter, ogcPropIsEmpty);
        ogcSetFilterPropertyLiteral(version, '', ogcPropIsEmpty);
        ogcOr.appendChild(ogcPropIsEmpty);
        return ogcOr;
    } else {
        return ogcPropIsNull;
    }
}

/**
 * Creates a OGC tag that excludes null and empty values
 * @param {any} filter the input filter
 * @returns {object} ogcAnd Tag
 */
function ogcExcludeNullAndEmpty(version, filter) {
    const createdOpengisNamespace = opengisNamespace(version);
    const ogcAnd = document.createElementNS(createdOpengisNamespace, 'And');
    const ogcNot = document.createElementNS(createdOpengisNamespace, 'Not');
    // PropertyIsNotNull does not exists, therefore use negation this way
    const ogcPropIsNull = document.createElementNS(createdOpengisNamespace, 'PropertyIsNull');
    // Set property name in OGC Filter
    ogcSetFilterPropertyName(version, filter, ogcPropIsNull);
    ogcNot.appendChild(ogcPropIsNull);
    const ogcPropIsNotEmpty = document.createElementNS(createdOpengisNamespace, 'PropertyIsNotEqualTo');
    // Set property name in OGC Filter
    ogcSetFilterPropertyName(version, filter, ogcPropIsNotEmpty);
    ogcSetFilterPropertyLiteral(version, '', ogcPropIsNotEmpty);
    ogcAnd.appendChild(ogcNot);
    ogcAnd.appendChild(ogcPropIsNotEmpty);
    return ogcAnd;
}

/**
 * Create a filter string for regular filter value and operator
 * @param  {*} filter, filter object on which the query ia based on
 * @return {string} query string
 */
function ogcCreateRegularPropertyOperator(version, filter) {
    const createdOpengisNamespace = opengisNamespace(version);
    let propertyoperator = createPropertyOperator(createdOpengisNamespace, filter);
    let filterValue = filter.value;

    if (['like', 'C', 'S', 'E'].includes(filter.operator)) {
        propertyoperator.setAttribute('escape', '\\');
        propertyoperator.setAttribute('singleChar', '_');
        propertyoperator.setAttribute('wildCard', '%');
        if (filter.operator === 'like' || filter.operator === 'C') {
            filterValue = `%${filter.value}%`;
        } else if (filter.operator === 'S') {
            filterValue = `${filter.value}%`;
        } else if (filter.operator === 'E') {
            filterValue = `%${filter.value}`;
        }
    }

    // Set property name in OGC Filter
    ogcSetFilterPropertyName(version, filter, propertyoperator);
    // Set property literal in OGC Filter
    ogcSetFilterPropertyLiteral(version, filterValue, propertyoperator);
    // Set matchCase in OGC Fiter
    if (!filter.omitCase) {
        ogcSetFilterMatchCase(filter, propertyoperator);
    }
    return propertyoperator;
}

function createPropertyOperator(createdOpengisNamespace, filter) {
    switch (filter.operator) {
        case '=':
            return document.createElementNS(createdOpengisNamespace, 'PropertyIsEqualTo');
        case '>':
            return document.createElementNS(createdOpengisNamespace, 'PropertyIsGreaterThan');
        case '<':
            return document.createElementNS(createdOpengisNamespace, 'PropertyIsLessThan');
        case '>=':
            return document.createElementNS(createdOpengisNamespace, 'PropertyIsGreaterThanOrEqualTo');
        case '<=':
            return document.createElementNS(createdOpengisNamespace, 'PropertyIsLessThanOrEqualTo');
        case '<>':
            return document.createElementNS(createdOpengisNamespace, 'PropertyIsNotEqualTo');
        case 'C':
        case 'like':
        case 'S':
        case 'E':
            return document.createElementNS(createdOpengisNamespace, 'PropertyIsLike');
        default:
            filter.operator = '=';
            return document.createElementNS(createdOpengisNamespace, 'PropertyIsEqualTo');
    }
}

/**
 * Set property literal (value) in OGC Filter
 * @param {any} filterValue filter value
 * @param {any} propertyOperator OGC property operator
 */
function ogcSetFilterPropertyLiteral(version, filterValue, propertyOperator) {
    const createdOpengisNamespace = opengisNamespace(version);
    const propertyLiteral = document.createElementNS(createdOpengisNamespace, 'Literal');
    propertyLiteral.appendChild(document.createTextNode(filterValue));

    propertyOperator.appendChild(propertyLiteral);
}

/**
 * Set property name (attribute/column) in OGC Filter
 * @param {any} filter filter options
 * @param {any} propertyOperator OGC property operator
 */
function ogcSetFilterPropertyName(version, filter, propertyOperator) {
    let propertyName;
    const createdOpengisNamespace = opengisNamespace(version);
    if (version < 2) {
        // WFS version 1.0.0 + 1.1.0
        propertyName = document.createElementNS(createdOpengisNamespace, 'PropertyName');
    } else {
        // WFS version 2.0.0 and newer
        propertyName = document.createElementNS(createdOpengisNamespace, 'ValueReference');
    }
    propertyName.appendChild(document.createTextNode(filter.filterAttribute));
    propertyOperator.appendChild(propertyName);
}

/**
 * Match case in OGC property operator when input filter is case-sensitive
 * @param {any} filter - the input filter
 * @param {any} propertyoperator - OGC property operator
 */
function ogcSetFilterMatchCase(filter, propertyoperator) {
    let matchCase = 'false';
    if (filter.caseSensitive) {
        matchCase = 'true';
    }
    propertyoperator.setAttribute('matchCase', matchCase);
}

/**
 * Change options to use intersects filter in stead of bbox filter
 * Exception: Cyclomedia WFS does not support intersects-filter
 * @param {object} options - the WFS getFeature options
 */
export function setIntersectsFilter(options) {
    // Cyclomedia WFS does not support Intersects-filter, keep it at BBOX
    if (options.featureTypes.includes('atlas:Recording')) {
        return;
    }
    options.filter = new OlIntersects(options.geometryName, options.bbox, 'EPSG:28992');
    options.bbox = null;
}
